import { DynamicList } from '@/interfaces/api/general/dynamicList';
import { User } from '@/interfaces/vuex/user';
import { computed, ref, onMounted, Ref } from 'vue';
import { StatusId } from '@/lib/enum';
import { type useTagComposable } from '@/composables/useTag';
import { useDepartmentComposable } from '@/composables/useDepartment';
import {
  SdsFiltersAndIncludes,
  UserSearchCriteria,
} from '@/interfaces/api/general/company';
import { i18n } from '@/plugins/vue-i18n';
import { NO_DEPARTMENT_ID } from '@/lib/constant';
import { buildDepartmentSelectOptions } from '@/utils/departmentTree';

const $t = i18n.tc.bind(i18n);

export const SDS_FILTER_LOCAL_STORAGE_KEY = 'sdsesFilterParams';

// 「作業環境入力推奨」フィルターの選択肢（値はバックエンドと一致させる）
export const WORKING_ENV_RECOMMENDATION = {
  RECOMMENDED: 'RECOMMENDED',
  COMPLETED: 'COMPLETED',
  NOT_RECOMMENDED: 'NOT_RECOMMENDED',
} as const;

// 健康有害性 / 物理化学的危険性リスクの選択肢（高リスク順）
export const RISK_LEVELS = [4, 3, 2, 1];

// リスクレベルに対応するチップ色（CompanySdsTableの表示と揃える）
export const getRiskLevelChipColor = (
  level: number | string | null
): string => {
  switch (Number(level)) {
    case 1:
      return '#5d87ff'; // --sea-500 (bg-status-inprogress)
    case 2:
      return '#2cac9f'; // --chemican-green-500 (bg-status-success)
    case 3:
      return '#ebb700'; // --yellow-500 (bg-status-warning)
    case 4:
      return '#ff5454'; // --sun-500 (bg-status-alert)
    default:
      return 'grey';
  }
};

// 作業環境入力推奨チップの色（base-status-class error/success 相当のテーマ色）
export const getWorkingEnvRecommendationChipColor = (
  value: string | number
): string => {
  switch (value) {
    case WORKING_ENV_RECOMMENDATION.RECOMMENDED:
      return 'error';
    case WORKING_ENV_RECOMMENDATION.COMPLETED:
      return 'success';
    default:
      // 推奨なし: 色指定なし = 担当ユーザーチップと同じデフォルトグレー
      return '';
  }
};

export type SdsesFilterParams = {
  statusGroupId: string[];
  assigneeUsers: string[];
  tagIds: number[];
  includeNoTag: boolean;
  departmentUuids: string[];
  dynamicListIds: number[];
  sdsName: string;
  productName: string;
  manufacturerName: string;
  productCode: string;
  revisedAtStartDate: string;
  revisedAtEndDate: string;
  createdAtStartDate: string;
  createdAtEndDate: string;
  isLatestOnly: boolean;
  workingEnvRecommendations: string[];
  healthHazardLevels: number[];
  physicalHazardLevels: number[];
};

export type SdsesFilterParamsKeys = keyof SdsesFilterParams;
export type SdsesFilterParamsValue =
  | string
  | number
  | boolean
  | string[]
  | number[]
  | (string | number)[]
  | undefined;

const defaultValue: SdsesFilterParams = {
  statusGroupId: [], // Keep the status filter
  assigneeUsers: [], // Add a filter for the assignee user
  tagIds: [], //Add a filter for the tags
  includeNoTag: false,
  departmentUuids: [], // Add a filter for the department
  dynamicListIds: [], // Add a filter for the dynamic list
  sdsName: '',
  productName: '',
  manufacturerName: '',
  productCode: '',
  revisedAtStartDate: '',
  revisedAtEndDate: '',
  createdAtStartDate: '',
  createdAtEndDate: '',
  isLatestOnly: false,
  workingEnvRecommendations: [],
  healthHazardLevels: [],
  physicalHazardLevels: [],
};

const selectableStatusList = StatusId.getDropdownOptions();

export const convertFilterParamsToRequest = (
  filterParams: SdsesFilterParams
): SdsFiltersAndIncludes => ({
  sds_name: filterParams.sdsName,
  product_name: filterParams.productName,
  manufacturer_name: filterParams.manufacturerName,
  product_code: filterParams.productCode,
  revised_at_start_date: filterParams.revisedAtStartDate,
  revised_at_end_date: filterParams.revisedAtEndDate,
  created_at_start_date: filterParams.createdAtStartDate,
  created_at_end_date: filterParams.createdAtEndDate,
  status_id_list: filterParams.statusGroupId.length
    ? selectableStatusList
        .filter((statusId) =>
          filterParams.statusGroupId.includes(statusId.groupId)
        )
        .map((statusId) => statusId.value)
    : StatusId.values(),
  user_id_list: filterParams.assigneeUsers,
  department_id_list: filterParams.departmentUuids.filter(
    (id) => id !== NO_DEPARTMENT_ID
  ),
  include_no_department:
    filterParams.departmentUuids.includes(NO_DEPARTMENT_ID),
  dynamic_list_id_list: filterParams.dynamicListIds,
  tag_id_list: filterParams.tagIds,
  include_no_tag: filterParams.includeNoTag,
  health_hazard_level_list: filterParams.healthHazardLevels,
  physical_hazard_level_list: filterParams.physicalHazardLevels,
  working_env_recommendation_list: filterParams.workingEnvRecommendations,
  is_latest_only: filterParams.isLatestOnly || false,
  //以下固定値
  has_product: true,
  has_section1: true,
  has_section2: false,
  has_section3: true,
  has_section8: false,
  has_section8_exposure_control: false,
  has_section9: false,
  has_section14: true,
  has_section15: false,

  //要確認
  has_assistant_log_cannot_analysis: false,
  has_assistant_log_reconfirmation: false,
});

export const useSdsFilter = (
  companyUsers: Ref<User[]>,
  tags: ReturnType<typeof useTagComposable>['tags'],
  departments: ReturnType<typeof useDepartmentComposable>['companyDepartments'],
  dynamicList: Ref<Array<DynamicList>>
) => {
  //params
  const currentFilterParams = ref<SdsesFilterParams>({ ...defaultValue });
  const formFilterParams = ref<SdsesFilterParams>({ ...defaultValue });

  const updateFormFilterParam = (
    key: SdsesFilterParamsKeys,
    value: SdsesFilterParamsValue
  ) => {
    formFilterParams.value = {
      ...formFilterParams.value,
      [key]: value,
    };
  };

  const applyFormFilterParams = () => {
    currentFilterParams.value = formFilterParams.value;

    try {
      localStorage.setItem(
        SDS_FILTER_LOCAL_STORAGE_KEY,
        JSON.stringify(formFilterParams.value)
      );
    } catch (e) {
      // do not anything
    }
  };

  const applyFromMyFilter = (criteria: UserSearchCriteria) => {
    formFilterParams.value = {
      statusGroupId: criteria.criteria.statusGroupId ?? [],
      assigneeUsers: criteria.criteria.assigneeUsers ?? [],
      tagIds: criteria.criteria.tagIds ?? [],
      includeNoTag: criteria.criteria.includeNoTag ?? false,
      departmentUuids: criteria.criteria.departmentUuids ?? [],
      dynamicListIds: criteria.criteria.dynamicListIds ?? [],
      sdsName: criteria.criteria.sdsName ?? '',
      productName: criteria.criteria.productName ?? '',
      manufacturerName: criteria.criteria.manufacturerName ?? '',
      productCode: criteria.criteria.productCode ?? '',
      revisedAtStartDate: criteria.criteria.revisedAtStartDate ?? '',
      revisedAtEndDate: criteria.criteria.revisedAtEndDate ?? '',
      createdAtStartDate: criteria.criteria.createdAtStartDate ?? '',
      createdAtEndDate: criteria.criteria.createdAtEndDate ?? '',
      isLatestOnly: criteria.criteria.isLatestOnly ?? false,
      workingEnvRecommendations:
        criteria.criteria.workingEnvRecommendations ?? [],
      healthHazardLevels: criteria.criteria.healthHazardLevels ?? [],
      physicalHazardLevels: criteria.criteria.physicalHazardLevels ?? [],
    };
  };

  const userOptions = computed(() =>
    companyUsers.value.map((user) => ({
      value: user.uuid,
      title: [user.last_name, user.middle_name, user.first_name]
        .filter((text) => Boolean(text))
        .join(' '),
    }))
  );

  // 階層順に並べ、罫線とフルパスを付ける。親が違えば同名の部署を作れるため、
  // 名前だけを並べるとどちらで絞り込んでいるのか分からない。
  const departmentOptions = computed(() =>
    buildDepartmentSelectOptions(departments.value)
  );

  const tagOptions = computed(() =>
    tags.value.map((tag) => ({
      value: tag.id,
      title: tag.name,
    }))
  );

  const statusOptions = computed(() => {
    return Array.from(
      new Set(
        selectableStatusList.map((status) => ({
          value: status.groupId,
          title: $t(status.messageId),
          state: StatusId.getStatusFromStatusId(status.value),
        }))
      )
    );
  });

  const dynamicListOptions = computed(() => {
    // SE-572: お気に入り(★)のチェックリストは黄色の★アイコンを付与。
    // SelectInTextField / TextTruncatedChip が isFavorite を見て描画するため、
    // title はプレーンな名前のまま、フラグだけ渡す。
    return dynamicList.value.map((d) => ({
      value: d.id,
      title: d.name,
      isFavorite: !!d.is_favorite,
    }));
  });

  const hasFilterParams = computed(() =>
    Boolean(
      currentFilterParams.value.statusGroupId.length > 0 ||
        currentFilterParams.value.assigneeUsers.length > 0 ||
        currentFilterParams.value.tagIds.length > 0 ||
        currentFilterParams.value.includeNoTag ||
        currentFilterParams.value.departmentUuids.length > 0 ||
        currentFilterParams.value.dynamicListIds.length > 0 ||
        currentFilterParams.value.sdsName ||
        currentFilterParams.value.productName ||
        currentFilterParams.value.manufacturerName ||
        currentFilterParams.value.productCode ||
        currentFilterParams.value.revisedAtStartDate ||
        currentFilterParams.value.revisedAtEndDate ||
        currentFilterParams.value.createdAtStartDate ||
        currentFilterParams.value.createdAtEndDate ||
        currentFilterParams.value.isLatestOnly ||
        currentFilterParams.value.workingEnvRecommendations.length > 0 ||
        currentFilterParams.value.healthHazardLevels.length > 0 ||
        currentFilterParams.value.physicalHazardLevels.length > 0
    )
  );

  onMounted(() => {
    try {
      const savedFilterParams = localStorage.getItem(
        SDS_FILTER_LOCAL_STORAGE_KEY
      );

      if (savedFilterParams) {
        const params = {
          //過去のデータがどうなってるかわからないので念の為defaultを入れる
          ...defaultValue,
          ...JSON.parse(savedFilterParams),
        };
        currentFilterParams.value = params;
        formFilterParams.value = params;
      }
    } catch (error) {
      //do not anything
    }
  });

  return {
    currentFilterParams,
    formFilterParams,
    updateFormFilterParam,
    applyFormFilterParams,
    userOptions,
    departmentOptions,
    tagOptions,
    statusOptions,
    dynamicListOptions,
    hasFilterParams,
    applyFromMyFilter,
  };
};

export type UseSdsFilter = ReturnType<typeof useSdsFilter>;
